import { marked } from "marked";
import DOMPurify from "dompurify";

/**
 * Escape a string so it is safe to interpolate into HTML as text.
 * Ampersand first so later replacements are not re-escaped.
 */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Real HTML tag names (formatting the AI draft pipeline emits, plus tags
 * DOMPurify must see in order to strip them). Anything else shaped like
 * `<Client Name>` / `<Effective Date>` is a legal placeholder — see
 * HTML_TAG_RE in markdown.utils.ts — and must not be treated as markup.
 */
const KNOWN_HTML_TAG_NAMES = new Set([
  "a",
  "article",
  "aside",
  "b",
  "blockquote",
  "br",
  "cite",
  "code",
  "dd",
  "del",
  "div",
  "dl",
  "dt",
  "em",
  "figcaption",
  "figure",
  "footer",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "header",
  "hr",
  "i",
  "img",
  "ins",
  "li",
  "main",
  "mark",
  "nav",
  "ol",
  "p",
  "pre",
  "q",
  "s",
  "section",
  "small",
  "span",
  "strong",
  "sub",
  "sup",
  "table",
  "tbody",
  "td",
  "tfoot",
  "th",
  "thead",
  "tr",
  "u",
  "ul",
  "script",
  "iframe",
  "object",
  "embed",
  "form",
  "input",
  "textarea",
  "button",
  "link",
  "meta",
  "style",
  "svg",
  "math",
  "video",
  "audio",
  "source",
  "base",
  "applet",
  "frame",
  "frameset",
]);

const PLACEHOLDER_TOKEN_RE = /<\/?\s*([^<>]*?)\s*>/g;

function isKnownHtmlTagToken(inner: string): boolean {
  const tagName = (String(inner).trim().split(/[\s/]/)[0] ?? "").toLowerCase();
  return tagName.length > 0 && KNOWN_HTML_TAG_NAMES.has(tagName);
}

function asHtmlEntities(value: string): string {
  return value.replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/**
 * Encode angle-bracket sequences that are not real HTML tags so DOMPurify
 * keeps them as text. Real tags are left untouched for the sanitiser.
 */
export function preserveLegalPlaceholders(input: string): string {
  return input.replace(PLACEHOLDER_TOKEN_RE, (match: string, inner: string) => {
    if (isKnownHtmlTagToken(inner)) {
      return match;
    }
    return asHtmlEntities(match);
  });
}

/**
 * Sanitise AI-drafted HTML for Word insertHtml / DOM sinks, without deleting
 * legal placeholders. Unknown tag-shaped tokens are parked as sentinels
 * across sanitise, then restored as `&lt;Client Name&gt;` entities so Word's
 * HTML parser also treats them as text rather than unknown tags.
 */
export function sanitizeDraftHtml(htmlContent: string): string {
  const saved: string[] = [];
  const withSentinels = htmlContent.replace(
    PLACEHOLDER_TOKEN_RE,
    (match: string, inner: string) => {
      if (isKnownHtmlTagToken(inner)) {
        return match;
      }
      saved.push(match);
      return `@@AILA_PH_${saved.length - 1}@@`;
    }
  );
  let sanitized = DOMPurify.sanitize(withSentinels);
  for (let i = 0; i < saved.length; i += 1) {
    const original = saved[i];
    if (original == null) {
      continue;
    }
    sanitized = sanitized.replace(`@@AILA_PH_${i}@@`, asHtmlEntities(original));
  }
  return sanitized;
}

/** One labelled paragraph whose values cannot break out of the tags. */
export function htmlFieldLine(label: string, value: string): string {
  return `<p><strong>${escapeHtml(label)}:</strong> ${escapeHtml(value)}</p>`;
}

/**
 * Convert markdown to HTML, then sanitise. Call this before posting HTML to
 * conversion or inserting it into Word — never pass marked() output through raw.
 */
export async function markedAndSanitised(markdown: string): Promise<string> {
  const htmlContent = await marked(markdown);
  return DOMPurify.sanitize(String(htmlContent));
}
